import * as _ from 'lodash';
import * as utils from './utils';
import * as config from './commons/config';
import * as perf from './commons/performance-logger';
import * as analytics from './commons/testimAnalytics';
import * as testimCustomToken from './commons/testimCustomToken';
import * as testimServicesApi from './commons/testimServicesApi';
import * as remoteStepPlayback from './stepPlayers/remoteStepPlayback';
import pRetry from 'p-retry';
import { URL } from 'node:url';
import { getLogger, getBetterStackCredentials } from './commons/logger';
import { getBuckets } from './commons/bucketConfig';
import { preloadTests } from './commons/preloadTests';
import { SeleniumPerfStats } from './commons/SeleniumPerfStats';
import { timeoutMessages, MOBILE_APP_SOURCE } from './commons/constants';
import { remoteStepService } from './commons/socket/remoteStepService';
import { testResultService } from './commons/socket/testResultService';
import { loadTest } from './commons/testimServicesApi';
import type { GridType } from './commons/constants';
import type { RunnerOptions } from './runOptions';
import type { CDPTestRunner } from './cdpTestRunner';
import type { TestRunStatus } from './testRunStatus';
import type { ExecutionList } from './runners/TestPlanRunner';
import type { AndroidApp, iOSApp } from 'clickim/src/background/mobile/types';
import type { RawMobileAppVersion } from 'services/src/commons/mongo/models/mobileAppVersion';
import type { TestWithCustomTestDataAndRunConfig } from 'services/src/commons/testListBuilder';
import type { ICapabilities } from 'services/src/commons/mongo/models/customCapabilities';
import type { RunParams as ClickimRunParams } from 'clickim/src/background/remoteRequestListener';
import type { TestOptimization } from '../../services/src/commons/mongo/models/testOptimization.ts';

const logger = getLogger('test-run-handler');
const RETRIES_ON_TIMEOUT = 3;
const MAX_LIGHTWEIGHT_MODE_RUN_DATA_SIZE = 20 * 1000; // max size, in characters, of stringified run data sent over URL params. Chosen arbitrarily, this value should be changed according to data.
const canSendRunDataOverUrl = (runData: object) => JSON.stringify(runData).length < MAX_LIGHTWEIGHT_MODE_RUN_DATA_SIZE;
const testCouldNotBeStarted = "Test couldn't be started";
type TestResult = utils.AwaitedReturnType<typeof testimServicesApi.getTestResults>;

export interface NativeAppData {
    name: string;
    activity?: string;
    packageName?: string;
    version: string;
    id?: string;
}

type GridAppPath =
    | 'testimTdcAppId'
    | 'testimTvcAppId'
    | 'browserStackAppId'
    | 'usSauceLabsAppId'
    | 'euSauceLabsAppId';

export class TestRunHandler {
    public readonly seleniumPerfStats = new SeleniumPerfStats();
    public _totalRetryCount = 1;
    private clearBrowser: true | undefined;
    private _sessionId: string | undefined;
    private _originalTestResultId: string | undefined;
    private _previousTestResultId: string | undefined;
    private _wasCompletedOnStartedCheck: TestResult | undefined;
    private _retryCount = 1;
    private _timeoutRetryCount = 1;
    private testInfraIssueRetryCount = 2;
    private isRerunOnTestStartTimeOutError = false;
    private clearTestResultFinished: Promise<{ success: boolean } | undefined> = Promise.resolve(undefined);
    private _baseUrl: string | undefined;
    private _testResultId;
    private readonly _testStatus;
    private readonly _testId;
    private readonly _testOptimization?: TestOptimization;
    private _nativeApp: RawMobileAppVersion | AndroidApp | iOSApp | undefined;
    private readonly _maxRetryCount;
    private readonly _remoteRunId;
    private readonly _runConfig;
    private readonly _testName: string | undefined;
    private readonly _overrideTestConfigId: string | undefined;
    private readonly _overrideTestConfig;
    private readonly _customCapabilities: ICapabilities | undefined;
    private _smartUIIntegrationData?: import('clickim/src/common/api/testimVisualTestingApi').LambdaTestIntegrationData;

    constructor(
        private readonly _executionId: string,
        private readonly _executionName: string,
        test: ExecutionList[number],
        private readonly _options: RunnerOptions,
        private readonly _branch: string,
        private readonly _testRunStatus: TestRunStatus,
    ) {
        this._testStatus = (test as TestWithCustomTestDataAndRunConfig).testStatus;
        this._testId = test.testId;
        this._testOptimization = (test as TestWithCustomTestDataAndRunConfig).testOptimization;
        this._testName = (test as TestWithCustomTestDataAndRunConfig).name;
        this._testResultId = test.resultId;
        this._baseUrl =
            _options.baseUrl ||
            (test as TestWithCustomTestDataAndRunConfig).baseUrl ||
            (test as TestWithCustomTestDataAndRunConfig).testConfig.baseUrl;
        this._nativeApp = (test as TestWithCustomTestDataAndRunConfig).nativeApp;
        this._overrideTestConfig =
            (test as any).overrideTestConfig || (test as TestWithCustomTestDataAndRunConfig).testConfig;
        this._overrideTestConfigId = (test as any).overrideTestConfig?.id;
        this._maxRetryCount = _options.retries;
        this._remoteRunId = _options.remoteRunId;

        this._customCapabilities =
            _options.customCapabilities ||
            (test as TestWithCustomTestDataAndRunConfig).customCapabilities?.capabilities;

        const shouldUpdateConfig = !(test as any).runConfig?.isMobileWeb && _options.browser;
        this._runConfig = shouldUpdateConfig
            ? utils.getRunConfigByBrowserName(_options.browser!, _options.saucelabs, _options.browserstack)
            : (test as TestWithCustomTestDataAndRunConfig).runConfig;
    }

    public async waitForExecutionStartedFinished() {
        await this._testRunStatus.waitForExecutionStartedFinished();
        return await this.clearTestResultFinished;
    }

    public get testStatus() {
        return this._testStatus;
    }

    public get runMode() {
        return this._options.mode;
    }

    public get runConfig() {
        return this._runConfig;
    }

    public get testResultId() {
        return this._testResultId;
    }

    public get baseUrl() {
        return this._baseUrl;
    }

    public set baseUrl(baseUrl) {
        this._baseUrl = baseUrl;
    }

    public set smartUIIntegrationData(data: import('clickim/src/common/api/testimVisualTestingApi').LambdaTestIntegrationData | undefined) {
        this._smartUIIntegrationData = data;
    }

    public get smartUIIntegrationData(): import('clickim/src/common/api/testimVisualTestingApi').LambdaTestIntegrationData | undefined {
        return this._smartUIIntegrationData;
    }

    public get executionId() {
        return this._executionId;
    }

    public get nativeApp() {
        return this._nativeApp as RawMobileAppVersion;
    }

    public get executionName() {
        return this._executionName;
    }

    public get customCapabilities() {
        return this._customCapabilities;
    }

    public getSauceLabsAppPath(host: string, app?: RawMobileAppVersion) {
        if (this.isAppFromDevice) {
            return null;
        }
        if (app) {
            return host.includes('us-west-1') ? app.usSauceLabsAppId : app.euSauceLabsAppId;
        }
        if (host.includes('us-west-1')) {
            return (this._nativeApp as RawMobileAppVersion)?.usSauceLabsAppId;
        }
        return (this._nativeApp as RawMobileAppVersion)?.euSauceLabsAppId;
    }
    // TODO:[2024-04-20] - remove this method and use getAppPathByGridType
    public getAppPath(path: GridAppPath, app?: RawMobileAppVersion) {
        if (this.isAppFromDevice) {
            return null;
        }
        if (app) {
            return app[path];
        }
        return (this._nativeApp as RawMobileAppVersion)?.[path];
    }
    public getAppPathByGridType(gridType: GridType, gridId: string, app?: RawMobileAppVersion) {
        if (this.isAppFromDevice) {
            return null;
        }
        if (app) {
            return app.gridData?.[gridType]?.[gridId];
        }
        if (this._nativeApp && 'gridData' in this._nativeApp) {
            return this._nativeApp.gridData?.[gridType]?.[gridId];
        }
        return null;
    }

    public get appPackageNameOrBundleId(): string | null {
        if (!this.nativeAppMetadata) {
            return null;
        }
        return this.nativeAppMetadata.id || this.nativeAppMetadata.packageName!;
    }

    public get androidActivityWait() {
        // activity always exists for android.
        const activity = this.nativeAppMetadata?.activity;
        if (!activity) {
            return null;
        }
        const lastValue = activity.split('.').pop()!;
        return activity.replace(lastValue, '*');
    }

    public get isAppFromDevice() {
        return utils.isAppFromDevice(this._nativeApp);
    }

    public get isAppForIosVirtualDevice() {
        const gridType = this._options.gridData?.type;
        return utils.shouldBlockVirtualIosBuild(this._nativeApp, gridType);
    }

    public get getAppSource() {
        if (!this._nativeApp) {
            return undefined;
        }
        return this.isAppFromDevice ? MOBILE_APP_SOURCE.FROM_DEVICE : MOBILE_APP_SOURCE.FROM_LIBRARY;
    }

    public get downloadableAppPublicLink() {
        if (!this._nativeApp) {
            return null;
        }
        if ('filePath' in this._nativeApp) {
            return `${config.SERVICES_HOST()}/storage${this._nativeApp.filePath}?access_token=${this._options.authData!.token}`;
        }
        return null;
    }

    public get mobileApp() {
        return this._nativeApp;
    }

    public set nativeApp(app: RawMobileAppVersion) {
        this._nativeApp = app;
    }

    public get nativeAppMetadata(): NativeAppData | null {
        if (!this._nativeApp) {
            return null;
        }
        if ('appMetadata' in this._nativeApp) {
            return this._nativeApp.appMetadata as NativeAppData;
        }
        return this._nativeApp as NativeAppData;
    }

    public get branch() {
        return this._branch;
    }

    public get sfdcCredential() {
        return this._options.sfdcCredential;
    }

    public get remoteRunId() {
        return this._remoteRunId;
    }

    public get overrideTestConfig() {
        return this._overrideTestConfig;
    }

    public get overrideTestConfigId() {
        return this._overrideTestConfigId;
    }

    public markClearBrowser() {
        this.clearBrowser = true;
    }

    public async isTestRecordedWithEnhancedMode() {
        const test = await loadTest({ projectId: this._options.project, testId: this.testId, branch: this.branch });

        return test.domMode === 'enhanced';
    }

    private async getRunRequestParams(): Promise<ClickimRunParams> {
        const customTokenV3 = await testimCustomToken.getCustomTokenV3();
        const runRequestParams: ClickimRunParams = {
            tokenV3: customTokenV3,
            refreshToken: testimCustomToken.getRefreshToken(),
            projectId: this._options.project,
            executionId: this._executionId,
            executionName: this._executionName,
            testId: this._testId,
            resultId: this._testResultId,
            baseUrl: this._baseUrl,
            branch: this._branch,
            servicesUrl: config.EXTENSION_SERVICES_HOST(),
            remoteRunId: this.remoteRunId,
            previousTestResultId: this.previousTestResultId,
            testRetryCount: this.retryCount,
            ...(this._options.shouldMonitorPerformance && { shouldMonitorPerformance: true }),
            ...(this._options.company && {
                companyId: this._options.company.companyId,
                planType: this._options.company.planType,
                isPOC: this._options.company.isPOC,
                isStartUp: this._options.company.isStartUp,
            }),
            ...(this._options.collectCodeCoverage && {
                codeCoverageUrlFilter: this._options.codeCoverageUrlFilter || `${this.baseUrl}*`,
            }),
            ...(this._options.disableMockNetwork && { disableMockNetwork: this._options.disableMockNetwork }),
            ...(this._options.lightweightMode && { lightweightMode: this._options.lightweightMode }),
            ...(this.clearBrowser && { clearBrowser: true }),
            ...(this._options.localRCASaver && { localRCASaver: this._options.localRCASaver }),
            ...(this.sfdcCredential && { sfdcCredential: this.sfdcCredential }),
            ...(getBetterStackCredentials() && { betterstack: getBetterStackCredentials() }),
            ...(this._smartUIIntegrationData && { smartUIIntegrationData: this._smartUIIntegrationData }),
            userAccessKey: await testimCustomToken.getUserAccessKey(),
            buckets: getBuckets(),
        };

        if (this._options.disableMockNetwork) {
            analytics.trackWithCIUser('user-disable-mock');
        }

        if (runRequestParams.lightweightMode && this._options.lightweightMode?.general) {
            Object.assign(runRequestParams, { company: this._options.company });
            const runData = this.runData;
            const isRunDataSentInUrl = canSendRunDataOverUrl(runData);
            Object.assign(runRequestParams.lightweightMode, { isRunDataSentInUrl });
            const stringifiedLength = JSON.stringify(runData).length;
            const testId = this.testId;
            if (isRunDataSentInUrl) {
                Object.assign(runRequestParams, { runData });

                logger.info(`Run data sent as URL param, test id: ${testId} run data length: ${stringifiedLength}`);
            } else {
                logger.warn(
                    `Run data is too big to be sent as a URL param. Test id: ${testId}, run data size: ${stringifiedLength} (limit: ${MAX_LIGHTWEIGHT_MODE_RUN_DATA_SIZE} characters)`,
                );
            }
            Object.assign(runRequestParams, {
                isLocalRun: Boolean(this._options.useLocalChromeDriver || this._options.useChromeLauncher),
            });
        }

        if (this._options.lightweightMode?.preloadTests && this._options.useChromeLauncher) {
            const preloadedTests = await preloadTests(this._options);
            Object.assign(runRequestParams, { preloadedTest: preloadedTests[runRequestParams.testId] });
        }

        return runRequestParams;
    }

    public async getRunTestUrl() {
        const runRequestParams = await this.getRunRequestParams();
        const url = `https://run.testim.io/?params=${encodeURIComponent(JSON.stringify(runRequestParams))}`;
        logger.info(`Test (${this.testId}) run URL length: ${url.length}`);
        return url;
    }

    public set sessionId(sessionId) {
        this._sessionId = sessionId;
    }

    public get sessionId() {
        return this._sessionId;
    }

    public get testId() {
        return this._testId;
    }

    public get testOptimization() {
        return this._testOptimization;
    }

    public get testName() {
        return this._testName;
    }

    public get runParams() {
        return this._options.runParams?.[this._testResultId] || {};
    }

    public get runData() {
        return {
            userParamsData: this.runParams,
            overrideTestConfigId: this._overrideTestConfigId || null,
        };
    }

    public async clearTestResult() {
        const runData = {
            ...this.runData,
            ...(this._options.mockNetworkRules && { mockNetworkRules: this._options.mockNetworkRules }),
        };
        const mustClearPreviousStepResults = this._timeoutRetryCount > 1 || this._retryCount > 1;

        if (
            this._options.lightweightMode?.disableResults &&
            !mustClearPreviousStepResults &&
            canSendRunDataOverUrl(runData)
        ) {
            return undefined;
        }

        const uploadRunDataArtifactAndFallbackToEmptyString = async () => {
            try {
                return await testimServicesApi.uploadRunDataArtifact(
                    this._options.project,
                    this._testId,
                    this._testResultId,
                    runData,
                );
            } catch (err) {
                logger.error('failed to upload run data artifact (runner)', { err });
                return '';
            }
        };
        const clearTestResultFinished = async () => {
            const runDataUrl = await uploadRunDataArtifactAndFallbackToEmptyString();
            // make sure the execution is created by now
            await this._testRunStatus.waitForExecutionStartedFinished();
            // we probably can save this backend call by initializing the execution
            return testimServicesApi.clearTestResult(this._options.project, this._testResultId, this._testId, {
                name: this._testName,
                resultId: this._testResultId,
                status: 'pending',
                retryCount: this._retryCount,
                runDataUrl, // links the run data url to the test.
                runData: runDataUrl ? undefined : runData, // put runData in mongo if we fail to upload to S3.
                testRetryKey: this.retryKey,
            });
        };
        this.clearTestResultFinished = clearTestResultFinished();
        return this.clearTestResultFinished;
    }

    public hasMoreRetries() {
        return this._retryCount < this._maxRetryCount!;
    }

    public isSeleniumHubNoMatchSessionError(reason: string) {
        const pattern = /Session \[([a-zA-Z0-9]+)\] not available and is not among the last \d+ terminated sessions/;
        return pattern.test(reason);
    }

    public isRerunOnInfraError(reason: string) {
        return (
            (this.isSeleniumHubNoMatchSessionError(reason) ||
                reason === testCouldNotBeStarted ||
                this.isRerunOnTestStartTimeOutError) &&
            this.testInfraIssueRetryCount
        );
    }

    public setReRunOnTestStartTimeOutError(value: boolean) {
        this.isRerunOnTestStartTimeOutError = value;
    }

    public decreaseInfraErrorRetry() {
        this.testInfraIssueRetryCount--;
    }

    public get retryOnInfraErrors() {
        return Boolean(this.testInfraIssueRetryCount);
    }

    public get retryKey(): `${number}:${number}` {
        return `${this._retryCount}:${this._timeoutRetryCount}`;
    }

    public startNewRetry() {
        this._retryCount++;
        this._timeoutRetryCount = 1;
        return this.onRetry();
    }

    public async runTestUsingCDP(cdpTestRunner: CDPTestRunner) {
        perf.log('runTestUsingCDP');
        const { targetInfos } = (await cdpTestRunner.cdpCommand<{
            targetInfos: { type: string; title: string; targetId: string }[];
        }>('Target.getTargets')) || { targetInfos: [] };
        const extensionNames = new Set([
            'Testim Editor',
            'Tricentis Testim Editor',
            'Service Worker chrome-extension://pebeiooilphfmbohdbhbomomkkoghoia/background/background.js',
        ]);
        const { targetId: extensionTargetId } =
            targetInfos.find(
                (target) =>
                    (target.type === 'background_page' || target.type === 'service_worker') &&
                    extensionNames.has(target.title),
            ) || {};
        const { targetId: AUTTargetId } = targetInfos.find((target) => target.type === 'page') || {};
        if (!extensionTargetId) {
            throw new Error('Tricentis Testim extension not found');
        }
        if (!AUTTargetId) {
            throw new Error('AUT target not found');
        }

        try {
            perf.log('before Target.attachToTarget');
            const [extensionSession, runRequestParams] = await Promise.all([
                cdpTestRunner.cdpCommand<{ sessionId: string } | undefined>('Target.attachToTarget', {
                    targetId: extensionTargetId,
                    flatten: true,
                }),
                this.getRunRequestParams(),
            ]);
            const { sessionId: extensionSessionId } = extensionSession || {};
            perf.log('before Runtime.evaluate');

            interface EvalResponse {
                result: { value?: any; subtype?: string; description?: string };
            }
            await pRetry(
                async () => {
                    const { result } = await cdpTestRunner.cdpCommand<EvalResponse>(
                        'Runtime.evaluate',
                        { expression: "typeof runTestimTest !== 'undefined'", returnByValue: true },
                        extensionSessionId,
                    );
                    if (!result.value) {
                        throw new Error('runTestimTest not available on global scope');
                    }
                },
                { retries: 100, minTimeout: 30, factor: 1 },
            );

            perf.log('after wait for runTestimTest function');
            const { result } = await cdpTestRunner.cdpCommand<EvalResponse>(
                'Runtime.evaluate',
                {
                    expression: `runTestimTest(${JSON.stringify(runRequestParams)})`,
                    awaitPromise: true,
                    returnByValue: true,
                },
                extensionSessionId,
            );
            if (result.subtype === 'error') {
                throw new Error(result.description);
            }
            perf.log('after Runtime.evaluate');
            return result.value as TestResult | undefined;
        } catch (err) {
            logger.error('error running test using CDP', { err });
            throw new Error('Error running test using CDP');
        }
    }

    private isRetryKeyMismatch(testResult: { testRetryKey?: string }) {
        return testResult.testRetryKey && testResult.testRetryKey !== this.retryKey;
    }

    public validateRunConfig() {
        const {
            baseUrl,
            runConfig: { browserValue },
        } = this;

        if (baseUrl && browserValue === 'safari') {
            let parsedUrl;
            try {
                parsedUrl = new URL(baseUrl);
            } catch {
                // ignore invalid URLs (missing http:// or https:// prefix)
                return;
            }
            const { username, password } = parsedUrl;

            if (username || password) {
                throw new Error('Basic authentication in URL is not supported in Safari');
            }
        }
    }

    public onStarted(startTimeout: number) {
        return new Promise<TestResult>((resolve) => {
            // We can't leave the test result as it may remove other listeners as well
            // We need to implement an .off(resultId, listener) method
            let reportedStart = false;
            const resolveResult = (testResult: TestResult) => {
                if (reportedStart) {
                    return;
                }
                if (this.isRetryKeyMismatch(testResult)) {
                    logger.warn(
                        `ignoring result update for on started due to retry key mismatch, got ${testResult.testRetryKey}, current is ${this.retryKey}`,
                        {
                            resultId: this.testResultId,
                            testId: this.testId,
                        },
                    );
                    return;
                }
                if (['running', 'completed'].includes(testResult.status)) {
                    testResult.resultId = this.testResultId;
                    if (testResult.status === 'completed' && !this.isRerunOnInfraError(testResult.reason!)) {
                        logger.info('setting _wasCompletedOnStartedCheck to true', {
                            testResult,
                            resultId: this.testResultId,
                            testId: this.testId,
                            testRetryKey: this.retryKey,
                        });
                        this._wasCompletedOnStartedCheck = testResult;
                    }
                    reportedStart = true;
                    resolve(testResult);
                }
            };
            if (this._options.disableSockets) {
                const timeLimit = Date.now() + startTimeout;
                const checkIfDone = async () => {
                    if (Date.now() > timeLimit) {
                        return;
                    }
                    const {
                        testId,
                        testResultId,
                        branch,
                        _options: { project: projectId },
                    } = this;

                    try {
                        const restResult = await testimServicesApi.getTestResults(
                            testId,
                            testResultId,
                            projectId,
                            branch,
                        );
                        resolveResult(restResult);
                        if (!reportedStart) {
                            setTimeout(checkIfDone, 3000);
                        }
                    } catch (err) {
                        logger.error('failed to check if done', { err });
                        setTimeout(checkIfDone, 3000);
                    }
                };
                setTimeout(checkIfDone, 3000);
            } else {
                testResultService.listenToTestResult(this._testResultId, this._testId, resolveResult);
            }
        });
    }

    public async checkViaRestAPIIfTestStarted() {
        const {
            testId,
            testResultId,
            _options: { project: projectId },
            branch,
        } = this;
        try {
            const testResult = await testimServicesApi.getTestResults(testId, testResultId, projectId, branch);
            const expectedStatuses = ['running', 'completed'];
            if (expectedStatuses.includes(testResult.status)) {
                logger.info(`get status: ${testResult.status} after not get test started status`, {
                    testId,
                    testResultId,
                    branch,
                });
                return testResult;
            }
            logger.error(
                `test not start test status: ${testResult.status} (expected [${expectedStatuses.join(', ')}])`,
                { testId, testResultId, branch },
            );
            throw new Error(timeoutMessages.TEST_START_TIMEOUT_MSG);
        } catch (err) {
            logger.error('failed to get test result after test start timeout', { err, testId, testResultId, branch });
            throw new Error(timeoutMessages.TEST_START_TIMEOUT_MSG);
        }
    }

    public async onCompletedCleanup() {
        if (this._options.disableSockets) {
            return undefined;
        }
        return await testResultService.leaveTestResult(this._testResultId, this._testId);
    }

    public async onCompleted() {
        let onConnected: _.DebouncedFunc<() => Promise<boolean>> | undefined;
        try {
            const res = await new Promise<TestResult>((resolve) => {
                if (this._wasCompletedOnStartedCheck && !this.isRetryKeyMismatch(this._wasCompletedOnStartedCheck)) {
                    logger.info('test was already completed in on started check', {
                        resultId: this.testResultId,
                        testId: this.testId,
                    });
                    resolve(this._wasCompletedOnStartedCheck);
                    return;
                }

                if (!this._options.disableSockets) {
                    testResultService.listenToTestResult(this._testResultId, this._testId, (testResult: TestResult) => {
                        if (this.isRetryKeyMismatch(testResult)) {
                            logger.warn(
                                `ignoring result update for on completed due to retry key mismatch, got ${testResult.testRetryKey}, current is ${this.retryKey}`,
                                {
                                    resultId: this.testResultId,
                                    testId: this.testId,
                                },
                            );
                            return;
                        }
                        if (testResult.status === 'completed') {
                            testResult.resultId = this._testResultId;
                            resolve(testResult);
                        }
                    });
                }
                const debounceDelay = this._options.disableSockets ? 0 : Math.floor(10_000 + Math.random() * 5_000);
                const maxWait = this._options.disableSockets ? 0 : Math.floor(60_000 + Math.random() * 15_000);
                onConnected = _.debounce(
                    async () => {
                        try {
                            await testResultService.joinToTestResult(this._testResultId, this.testId);
                        } catch (err) {
                            logger.error('failed joining to test result updates after socket reconnected', {
                                error: err,
                            });
                        }
                        try {
                            const testResult = await testimServicesApi.getTestResults(
                                this._testId,
                                this._testResultId,
                                this._options.project,
                                this.branch,
                            );
                            if (this.isRetryKeyMismatch(testResult)) {
                                logger.warn(
                                    `ignoring result update for on completed (in reconnect) due to retry key mismatch, got ${testResult.testRetryKey}, current is ${this.retryKey}`,
                                    {
                                        resultId: this.testResultId,
                                        testId: this.testId,
                                    },
                                );
                                return false;
                            }
                            if (testResult?.status === 'completed') {
                                logger.info('Socket reconnected - Test complete', {
                                    testId: this._testId,
                                    resultId: this._testResultId,
                                    projectId: this._options.project,
                                });
                                testResult.resultId = this._testResultId;
                                resolve(testResult);
                                return true;
                            }
                            return false;
                        } catch (err) {
                            logger.warn('Error while trying to check status on socket connect', err);
                            return false;
                        }
                    },
                    debounceDelay,
                    { maxWait },
                );
                if (!this._options.disableSockets) {
                    testResultService.on('socket-connected', onConnected);
                } else {
                    const waitForTestEnd = () => {
                        setTimeout(async () => {
                            try {
                                const { isComplete } = await testimServicesApi.isTestResultCompleted(
                                    this._testResultId,
                                    this._options.project,
                                    this.retryKey,
                                );
                                if (isComplete) {
                                    const isDone = await onConnected!();
                                    if (!isDone) {
                                        logger.warn('onConnected returned false even though isComplete was true');
                                        waitForTestEnd();
                                    }
                                } else {
                                    waitForTestEnd();
                                }
                            } catch (err) {
                                logger.error('failed to check is complete', { err });
                                waitForTestEnd();
                            }
                        }, 3000);
                    };
                    waitForTestEnd();
                }
            });
            await this.onCompletedCleanup();
            return res;
        } finally {
            if (onConnected && !this._options.disableSockets) {
                testResultService.off('socket-connected', onConnected);
            }
        }
    }

    public listenToRemoteStep(
        driver: import('./player/webdriver').WebDriver | import('./player/chromeLauncherTestPlayer').LauncherDriverType,
    ) {
        remoteStepService.listenToRemoteStep(this.testResultId, (step) => {
            remoteStepPlayback.executeStep(this._options, driver, step, this.testResultId);
        });
    }

    public hasMoreTimeoutRetries() {
        const maxRetryCount = this._options.disableTimeoutRetry ? 1 : RETRIES_ON_TIMEOUT;
        return this._timeoutRetryCount < maxRetryCount;
    }

    public decreaseRetryCount() {
        this._retryCount--;
        this._totalRetryCount--;
    }

    public startNewTimeoutRetry() {
        this._timeoutRetryCount++;
        return this.onRetry();
    }

    public get retryCount() {
        return this._retryCount;
    }

    public get previousTestResultId() {
        return this._previousTestResultId;
    }

    private isAllowReportTestResultRetries() {
        return Boolean(this._options.company?.activePlan?.premiumFeatures?.allowReportTestResultRetries);
    }

    private async onRetry() {
        this._previousTestResultId = this.testResultId;

        if (!this.isAllowReportTestResultRetries()) {
            return;
        }

        this._totalRetryCount++;
        this._originalTestResultId ||= this._previousTestResultId;
        this._testResultId = utils.guid();

        if (this._options.lightweightMode?.onlyTestIdsNoSuite) {
            return;
        }

        await this._testRunStatus.addRetryTestResult({
            retryCount: this._totalRetryCount,
            executionId: this._executionId,
            projectId: this._options.project,
            newResultId: this._testResultId,
            originalTestResultId: this._originalTestResultId,
            previousTestResultId: this._previousTestResultId,
        });
    }
}
